import { useState, useMemo, useEffect, useCallback } from 'react';
import { SIZE_OPTIONS, LENGTH_OPTIONS } from '../constants/serviceOptions';
import type { PublicBookingService } from '../types/publicBooking';

/** Services that come back with no category are grouped under this heading. */
export const OTHERS_CATEGORY = 'Others';

/** Pseudo-category for the "show everything" filter pill. */
export const ALL_CATEGORIES = 'All';

export interface ServiceCostLine {
	id: number;
	name: string;
	price: number;
	durationMinutes: number;
}

const categoryOf = (service: PublicBookingService): string =>
	service.category?.trim() || OTHERS_CATEGORY;

/**
 * Orders a set of size/length codes by the canonical catalogue order so the
 * pills always read S → M → L rather than in whatever order the API returned
 * the pricing rows. Codes outside the catalogue keep their relative order and
 * are appended.
 */
const orderByCatalogue = (values: Set<string>, catalogue: string[]): string[] => {
	const known = catalogue.filter(v => values.has(v));
	const unknown = [...values].filter(v => !catalogue.includes(v));
	return [...known, ...unknown];
};

/**
 * Drives the service → size → length cascade shared by both public booking
 * pages.
 *
 * The create API takes many `serviceIds` but a single `size` and `length`, so
 * the offered sizes are the ones every selected service can actually be priced
 * at (the intersection of their pricing combinations). That keeps the cost
 * breakdown complete and the payload valid — a size only one of two selected
 * services supports would otherwise produce a booking we can't price.
 */
export const usePublicBookingSelection = (services: PublicBookingService[]) => {
	const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORIES);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [size, setSize] = useState('');
	const [length, setLength] = useState('');

	// ── Categories ──
	const categories = useMemo(() => {
		const seen: string[] = [];
		services.forEach(s => {
			const cat = categoryOf(s);
			if (!seen.includes(cat)) seen.push(cat);
		});
		// "Others" always sits last, however the API ordered the services.
		const ordered = seen.filter(c => c !== OTHERS_CATEGORY);
		if (seen.includes(OTHERS_CATEGORY)) ordered.push(OTHERS_CATEGORY);
		return [ALL_CATEGORIES, ...ordered];
	}, [services]);

	const selectedServices = useMemo(
		() => selectedIds.map(id => services.find(s => s.id === id)).filter(Boolean),
		[selectedIds, services]
	) as PublicBookingService[];

	// ── Size / length options (intersection across selected services) ──
	const sizeOptions = useMemo(() => {
		if (selectedServices.length === 0) return [];
		const perService = selectedServices.map(
			s => new Set(s.pricingCombinations.map(pc => pc.size))
		);
		const shared = new Set(
			[...perService[0]].filter(candidate => perService.every(set => set.has(candidate)))
		);
		return orderByCatalogue(shared, SIZE_OPTIONS);
	}, [selectedServices]);

	const lengthOptions = useMemo(() => {
		if (selectedServices.length === 0 || !size) return [];
		const perService = selectedServices.map(
			s => new Set(s.pricingCombinations.filter(pc => pc.size === size).map(pc => pc.length))
		);
		const shared = new Set(
			[...perService[0]].filter(candidate => perService.every(set => set.has(candidate)))
		);
		return orderByCatalogue(shared, LENGTH_OPTIONS);
	}, [selectedServices, size]);

	// Adding or removing a service can invalidate an earlier pick — drop it
	// rather than silently submitting a size the new service isn't priced for.
	useEffect(() => {
		if (size && !sizeOptions.includes(size)) {
			setSize('');
			setLength('');
		}
	}, [size, sizeOptions]);

	useEffect(() => {
		if (length && !lengthOptions.includes(length)) setLength('');
	}, [length, lengthOptions]);

	// ── Pricing ──
	const costLines = useMemo<ServiceCostLine[]>(() => {
		if (!size || !length) return [];
		return selectedServices.map(service => {
			const combo = service.pricingCombinations.find(
				pc => pc.size === size && pc.length === length
			);
			return {
				id: service.id,
				name: service.name,
				price: combo?.price ?? 0,
				durationMinutes: combo?.durationMinutes ?? 0,
			};
		});
	}, [selectedServices, size, length]);

	const serviceTotal = useMemo(
		() => costLines.reduce((total, line) => total + line.price, 0),
		[costLines]
	);

	// ── Actions ──
	const toggleService = useCallback((id: number) => {
		setSelectedIds(prev =>
			prev.includes(id) ? prev.filter(existing => existing !== id) : [...prev, id]
		);
	}, []);

	const removeService = useCallback((id: number) => {
		setSelectedIds(prev => prev.filter(existing => existing !== id));
	}, []);

	const handleSizeChange = useCallback((value: string) => {
		setSize(value);
		setLength('');
	}, []);

	/** True when the selected services share no size at all — a dead end worth explaining. */
	const hasNoSharedSize = selectedServices.length > 0 && sizeOptions.length === 0;

	/**
	 * Sharing a size does not guarantee sharing a length at that size: one
	 * service may only be priced M/SHOULDER and another only M/WAIST.
	 */
	const hasNoSharedLength = Boolean(size) && lengthOptions.length === 0;

	return {
		categories,
		activeCategory,
		setActiveCategory,
		selectedIds,
		selectedServices,
		toggleService,
		removeService,
		size,
		setSize: handleSizeChange,
		length,
		setLength,
		sizeOptions,
		lengthOptions,
		hasNoSharedSize,
		hasNoSharedLength,
		costLines,
		serviceTotal,
	};
};

export default usePublicBookingSelection;
